/**
 * DOCUMENTATION:
 * Component: GCPDashboard
 * Purpose: Central hub for the Google Cloud Professional Architect track.
 *
 * Note for Junior Developers:
 * - GCP is organized by "Projects." Everything you build must live in a project.
 * - The "Global Backbone": Google owns its own private fiber-optic network,
 *   which is a key differentiator from AWS and Azure.
 * - SRE (Site Reliability Engineering): This certification heavily follows
 *   Google's SRE principles.
 *
 * STYLING NOTE: colors and card/button styles here intentionally match
 * JavaScriptCoding.js (the goldenrod-on-dark-purple theme), so every
 * track dashboard shares one consistent visual identity across the app.
 */

import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Grid, Card, Button, Stack, styled, useTheme, useMediaQuery, TextField, InputAdornment
} from "@mui/material";

/* Icons */
import CloudIcon from "@mui/icons-material/Cloud";
import SearchIcon from "@mui/icons-material/Search";

/* Shared Components */
import Typography from "../../../components/Typography";
import LanguageTabs from "../../../components/LanguageTabs";
import LectureTimer from "../../../components/LectureTimer";
import RadioOption from "../../../components/RadioOption";

const bgImage = "https://images.unsplash.com/photo-1544197150-b99a580bb7a8"; // Modern Data Center

/* Styled Components — matches JavaScriptCoding.js's goldenrod/dark-purple theme */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.90), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(12),
        paddingBottom: theme.spacing(5),
    },
}));

const DashboardGlassCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    height: "100%",
}));

const TopicBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    color: "white",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "10px",
    padding: theme.spacing(1.5, 2),
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "rgba(218, 165, 32, 0.1)",
        borderColor: "goldenrod",
    },
}));

const ViewButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 700,
    borderRadius: "999px",
    padding: "6px 20px",
    textTransform: "none",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

export default function GCPDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [searchQuery, setSearchQuery] = useState("");

    const lessons = useMemo(() => [
        { title: "GCP Resource Hierarchy & IAM", link: "/gcp-iam" },
        { title: "Virtual Private Cloud (VPC) Deep Dive", link: "/gcp-networking" },
        { title: "Compute Engine & Autoscaling", link: "/gcp-compute" },
        { title: "Google Kubernetes Engine (GKE)", link: "/gcp-gke" },
        { title: "Cloud Run & Serverless Architectures", link: "/gcp-serverless" },
        { title: "Storage Choices: SQL, Spanner & Bigtable", link: "/gcp-storage" },
        { title: "BigQuery & Modern Data Warehousing", link: "/gcp-bigdata" },
        { title: "Cloud Operations & SRE Principles", link: "/gcp-operations" },
        { title: "PCA Case Study: Mountkirk Games", link: "/gcp-case-study" },
        { title: "GCP PCA Final Certification Quiz", link: "/gcp-quiz" },
    ], []);

    const filteredLessons = useMemo(() => {
        const safeQuery = (searchQuery || "").toLowerCase();
        return lessons.filter(l => l.title.toLowerCase().includes(safeQuery));
    }, [searchQuery, lessons]);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 4, mt: 16, px: 2 }}>
                <Typography
                    variant={isMobile ? "h6" : "h3"}
                    sx={{
                        fontWeight: 700,
                        color: "goldenrod",
                        px: { xs: 3, md: 6 },
                        py: 1,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    GCP CLOUD ARCHITECT
                </Typography>
            </Box>

            <LanguageTabs active="GCP Architect" />

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 }, mt: 2 }}>
                <Grid item xs={12} md={5}>
                    <DashboardGlassCard>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, textAlign: "center", mb: 2, color: "goldenrod" }}
                        >
                            CERTIFICATION MISSION
                        </Typography>
                        <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.05)", borderRadius: "12px" }}>
                            <CloudIcon sx={{ fontSize: 60, color: "goldenrod", mb: 2 }} />
                            <Typography variant="body2" sx={{ lineHeight: 1.8, opacity: 0.8 }}>
                                To become a Google Cloud Architect, you must master the art of designing systems
                                that are secure, cost-effective, and global. You aren't just a coder anymore;
                                you are the bridge between business problems and cloud solutions.
                            </Typography>
                        </Box>
                        <Box sx={{ mt: 3 }}>
                            <LectureTimer />
                        </Box>
                    </DashboardGlassCard>
                </Grid>

                <Grid item xs={12} md={7}>
                    <DashboardGlassCard>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search GCP concepts..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ mb: 3 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "goldenrod" }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    color: "white",
                                    bgcolor: "rgba(255,255,255,0.05)",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(218, 165, 32, 0.3)" },
                                },
                            }}
                        />

                        <Stack spacing={1.5}>
                            {filteredLessons.length > 0 ? (
                                filteredLessons.map((lesson, i) => (
                                    <TopicBox key={i}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <RadioOption />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {lesson.title}
                                            </Typography>
                                        </Box>
                                        <Link to={lesson.link} style={{ textDecoration: "none" }}>
                                            <ViewButton size="small">Launch</ViewButton>
                                        </Link>
                                    </TopicBox>
                                ))
                            ) : (
                                <Typography sx={{ textAlign: "center", opacity: 0.5, py: 4 }}>
                                    No lessons found for "{searchQuery}"
                                </Typography>
                            )}
                        </Stack>
                    </DashboardGlassCard>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
